import { useState, useEffect, useMemo } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { request } from '@/lib/request'
import { toast } from 'sonner'
import QRCode from 'qrcode'
import { getOrderStatus } from '@/service/api/orders'
import { getRechargePresets, getPayChannels, PayChannelOption } from '@/service/api/pay'
import { useConfigStore } from '@/store/config'
import { DEFAULT_RECHARGE_PRESET_AMOUNTS } from '@/service/constants/system'
import { Loader2, Wallet } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  dialogHeader,
  dialogShell,
  formInput,
  gradientBtn,
} from '@/lib/ui-classes'

const PAY_CHANNEL_LOGOS: Record<number, { src: string; alt: string }> = {
  1: { src: '/images/wepay.svg', alt: '微信支付' },
  2: { src: '/images/alipay.svg', alt: '支付宝' },
}

export default function RechargePanel({ onClose }: { onClose?: () => void }) {
    const [loading, setLoading] = useState(false)
    const [qrCode, setQrCode] = useState<string | null>(null)
    const [payDialogOpen, setPayDialogOpen] = useState(false)
    const [amount, setAmount] = useState<number | string>(10)
    const [orderId, setOrderId] = useState<string | null>(null)
    const [payStatus, setPayStatus] = useState<'pending' | 'success'>('pending')
    const [presetAmounts, setPresetAmounts] = useState<number[]>(DEFAULT_RECHARGE_PRESET_AMOUNTS)
    const [presetsLoading, setPresetsLoading] = useState(true)
    const [channels, setChannels] = useState<PayChannelOption[]>([])
    const [payChannel, setPayChannel] = useState<number>(1)

    const isMobile = () => {
        if (typeof window === 'undefined') return false
        return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    }

    const handleCreateOrder = async () => {
        const finalAmount = Number(amount)
        if (!finalAmount || finalAmount <= 0) {
            toast.error('请输入有效的充值金额')
            return
        }

        setLoading(true)
        try {
            const mobile = isMobile()
            const client_type = mobile ? 'h5' : 'native'
            // Convert Yuan to Fen
            const amountInFen = Math.round(finalAmount * 100)

            // 根据所选支付渠道调用对应下单接口
            const endpoint = payChannel === 2 ? '/api/pay/alipay' : '/api/pay/wechat'

            const data = await request<{ order_id: string, pay_url: string }>(endpoint, {
                method: 'POST',
                body: JSON.stringify({ amount: amountInFen, client_type })
            })

            if (!data) {
                throw new Error('创建订单失败：未收到返回数据')
            }

            console.log('Order created:', data)
            setOrderId(data.order_id)
            setPayStatus('pending')

            if (mobile) {
                window.location.href = data.pay_url
            } else {
                if (!data.pay_url) {
                    throw new Error('未获取到支付链接')
                }
                // 微信返回 code_url、支付宝返回二维码内容，均生成二维码展示
                const qr = await QRCode.toDataURL(data.pay_url)
                setQrCode(qr)
                setPayDialogOpen(true)
            }

        } catch (e: any) {
            console.error('Recharge failed:', e)
        } finally {
            setLoading(false)
        }
    }

    const stateConfig = useConfigStore(state => state.config)
    const virtualCurrency = stateConfig?.virtual_currency
    const exchangeRate = stateConfig?.exchange_rate || 100

    const PACKAGES = useMemo(
        () =>
            presetAmounts.map((pkgAmount) => ({
                amount: pkgAmount,
                label: `${pkgAmount * exchangeRate} ${virtualCurrency}`,
                price: `￥${pkgAmount.toFixed(2)}`,
            })),
        [presetAmounts, exchangeRate, virtualCurrency]
    )

    useEffect(() => {
        let cancelled = false
        const loadPresets = async () => {
            setPresetsLoading(true)
            try {
                const res = await getRechargePresets()
                if (!cancelled && res?.amounts?.length) {
                    setPresetAmounts(res.amounts)
                    setAmount(res.amounts[0])
                }
            } catch (e) {
                console.error('Failed to load recharge presets', e)
                if (!cancelled) {
                    setPresetAmounts(DEFAULT_RECHARGE_PRESET_AMOUNTS)
                    setAmount(DEFAULT_RECHARGE_PRESET_AMOUNTS[0])
                }
            } finally {
                if (!cancelled) setPresetsLoading(false)
            }
        }
        loadPresets()

        const loadChannels = async () => {
            try {
                const res = await getPayChannels()
                if (!cancelled && res?.channels?.length) {
                    setChannels(res.channels)
                    setPayChannel(res.channels[0].pay_channel)
                }
            } catch (e) {
                console.error('Failed to load pay channels', e)
            }
        }
        loadChannels()

        return () => {
            cancelled = true
        }
    }, [])

    useEffect(() => {
        let timer: NodeJS.Timeout

        if (payDialogOpen && orderId && payStatus === 'pending') {
            const checkStatus = async () => {
                try {
                    const res = await getOrderStatus(orderId)
                    if (res && res.status === 'SUCCESS') {
                        setPayStatus('success')
                        // toast.success('充值成功')
                    }
                } catch (e) {
                    console.error('Check status failed', e)
                }
            }
            // 每2秒轮训一次
            timer = setInterval(checkStatus, 2000)
        }

        return () => {
            if (timer) clearInterval(timer)
        }
    }, [payDialogOpen, orderId, payStatus])

    return (
        <div className="overflow-hidden">
            <div className="relative border-b border-white/5 bg-gradient-to-br from-[#07F4F2]/15 via-[#C1FAB8]/5 to-transparent px-6 pt-6 pb-5">
                <div className="pointer-events-none absolute -right-6 -top-10 h-28 w-28 rounded-full bg-[#07F4F2]/20 blur-3xl" />
                <div className="pointer-events-none absolute -bottom-6 -left-4 h-20 w-20 rounded-full bg-[#C1FAB8]/10 blur-2xl" />

                <DialogHeader className="relative space-y-0 text-left">
                    <div className="flex items-start gap-3.5">
                        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[#07F4F2]/30 bg-gradient-to-br from-[#07F4F2]/25 to-[#C1FAB8]/15 shadow-lg shadow-[#07F4F2]/10">
                            <Wallet className="h-5 w-5 text-[#07F4F2]" />
                        </div>
                        <div className="min-w-0 flex-1 pt-0.5">
                            <DialogTitle className="bg-gradient-to-r from-white via-[#07F4F2] to-[#C1FAB8] bg-clip-text text-xl font-bold tracking-tight text-transparent">
                                充值中心
                            </DialogTitle>
                            <p className="mt-0.5 text-xs text-zinc-500">安全便捷 · 即时到账</p>
                        </div>
                    </div>
                </DialogHeader>
            </div>

            <div className="px-6 py-6 space-y-5">
                <div className="grid grid-cols-3 gap-3">
                    {presetsLoading ? (
                        <div className="col-span-3 flex items-center justify-center py-8 text-zinc-500">
                            <Loader2 className="mr-2 h-5 w-5 animate-spin text-[#07F4F2]" />
                            加载充值档位...
                        </div>
                    ) : PACKAGES.map((pkg) => {
                        const isSelected = Number(amount) === pkg.amount
                        return (
                            <div
                                key={pkg.amount}
                                className={cn(
                                    'relative group cursor-pointer rounded-xl border p-3 text-center transition-all duration-300 overflow-hidden',
                                    isSelected
                                        ? 'border-[#07F4F2]/50 shadow-[0_0_20px_-5px_rgba(7,244,242,0.25)]'
                                        : 'border-white/10 hover:border-white/30'
                                )}
                                onClick={() => setAmount(pkg.amount)}
                            >
                                <div className="absolute inset-0 transition-all duration-300">
                                    <div className={cn(
                                        'absolute inset-0 bg-gradient-to-br',
                                        isSelected
                                            ? 'from-[#07F4F2]/30 via-[#C1FAB8]/15 to-transparent'
                                            : 'from-zinc-800/60 via-zinc-900/60 to-transparent group-hover:from-zinc-800 group-hover:via-zinc-900'
                                    )} />
                                </div>

                                <div className="relative z-10">
                                    {isSelected && (
                                        <div className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-gradient-to-r from-[#07F4F2] to-[#C1FAB8] rounded-full flex items-center justify-center shadow-md">
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" className="w-3 h-3 text-black">
                                                <polyline points="20 6 9 17 4 12" />
                                            </svg>
                                        </div>
                                    )}
                                    <div className={cn(
                                        'text-sm font-medium mb-1 transition-colors',
                                        isSelected ? 'text-[#07F4F2]' : 'text-zinc-400 group-hover:text-zinc-300'
                                    )}>
                                        {pkg.label}
                                    </div>
                                    <div className={cn(
                                        'text-lg font-bold font-mono transition-colors',
                                        isSelected ? 'text-white' : 'text-zinc-200'
                                    )}>
                                        {pkg.price}
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                </div>

                <div className="bg-black/25 border border-white/10 rounded-xl p-4">
                    <Label className="text-sm font-medium mb-3 block text-zinc-400">自定义金额</Label>
                    <div className="flex items-center gap-3">
                        <div className="relative flex-1">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 font-mono">￥</span>
                            <Input
                                type="number"
                                min="1"
                                step="1"
                                value={amount}
                                onChange={(e) => {
                                    const val = e.target.value
                                    if (!val || /^\d+$/.test(val)) {
                                        setAmount(val)
                                    }
                                }}
                                className={cn(formInput, 'pl-7 font-mono text-lg h-10')}
                                placeholder="100"
                            />
                        </div>
                        <div className="text-zinc-500 whitespace-nowrap text-sm flex items-center gap-1.5 bg-black/25 px-3 py-2 rounded-lg border border-white/10">
                            <span>获得</span>
                            <span className="text-[#07F4F2] font-bold font-mono text-lg">
                                {Math.floor(Number(amount || 0) * exchangeRate)}
                            </span>
                            <span className="text-xs">{virtualCurrency}</span>
                        </div>
                    </div>
                </div>

                {channels.length > 1 && (
                    <div className="bg-black/25 border border-white/10 rounded-xl p-4">
                        <Label className="text-sm font-medium mb-3 block text-zinc-400">支付方式</Label>
                        <div className="grid grid-cols-2 gap-3">
                            {channels.map((ch) => {
                                const isSelected = payChannel === ch.pay_channel
                                const logo = PAY_CHANNEL_LOGOS[ch.pay_channel]
                                const label = ch.name || logo?.alt || '支付'
                                return (
                                    <button
                                        key={ch.pay_channel}
                                        type="button"
                                        onClick={() => setPayChannel(ch.pay_channel)}
                                        aria-label={label}
                                        aria-pressed={isSelected}
                                        className={cn(
                                            'relative overflow-hidden rounded-lg border transition-all duration-300',
                                            'h-11 px-2 py-1.5 bg-white/5',
                                            isSelected
                                                ? 'border-[#07F4F2]/50 shadow-[0_0_20px_-5px_rgba(7,244,242,0.25)] ring-1 ring-[#07F4F2]/30'
                                                : 'border-white/10 opacity-70 hover:opacity-100 hover:border-white/30'
                                        )}
                                    >
                                        {logo ? (
                                            <img
                                                src={logo.src}
                                                alt={logo.alt}
                                                className="h-full w-full object-contain object-center"
                                            />
                                        ) : (
                                            <span className="text-sm font-medium text-zinc-300">{label}</span>
                                        )}
                                    </button>
                                )
                            })}
                        </div>
                    </div>
                )}

                <button
                    type="button"
                    className={cn(gradientBtn, 'w-full h-11 text-base !rounded-lg gap-2 disabled:opacity-50 disabled:cursor-not-allowed')}
                    onClick={handleCreateOrder}
                    disabled={loading}
                >
                    {loading ? (
                        <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            正在创建订单...
                        </>
                    ) : (
                        `立即支付 ￥${Number(amount || 0).toFixed(2)}`
                    )}
                </button>
            </div>

            {/* QR Code Dialog */}
            <Dialog
                open={payDialogOpen}
                onOpenChange={(open) => {
                    setPayDialogOpen(open)
                    if (!open && payStatus === 'success' && onClose) {
                        onClose()
                    }
                }}
            >
                <DialogContent className={cn(dialogShell, 'sm:max-w-[300px] overflow-hidden')} onInteractOutside={(e) => e.preventDefault()}>
                    <DialogHeader className={dialogHeader}>
                        <DialogTitle className="text-zinc-100">{payChannel === 2 ? '支付宝' : '微信支付'}</DialogTitle>
                    </DialogHeader>
                    <div className="flex flex-col items-center justify-center py-6 space-y-4">
                        <div className="relative w-48 h-48">
                            {qrCode && <img src={qrCode} alt="Payment QR" className="w-full h-full border border-white/10 rounded-lg" />}
                            {payStatus === 'success' && (
                                <div className="absolute inset-0 z-10 bg-black/80 flex flex-col items-center justify-center rounded-lg animate-in fade-in duration-300">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#07F4F2] mb-2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
                                    <span className="text-[#07F4F2] font-bold text-md">支付成功</span>
                                </div>
                            )}
                        </div>
                        <p className="text-sm text-zinc-500 text-center">
                            请使用{payChannel === 2 ? '支付宝' : '微信'}扫一扫<br />
                            支付 ￥{Number(amount || 0).toFixed(2)}
                        </p>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    )
}
